<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const LABELS = {
  title: 'Meta Conversions API',
  description:
    'Envie eventos de conversão (Purchase / Lead) de volta pro Meta Ads Manager. Fecha o ciclo de atribuição de anúncios CTWA — Meta otimiza campanhas pra encontrar mais leads como os que fecharam venda.',
  backToIntegrations: 'Voltar para integrações',
  sectionConfig: 'Configuração',
  sectionEvents: 'Eventos enviados',
  sectionTutorial: 'Como configurar (passo a passo)',
  tutorialIntro:
    'Esses passos pegam o Pixel ID e o Access Token que você precisa pro form abaixo. Faça uma vez por cliente — depois é só salvar.',
  tutorialStep1Title: '1. Acesse o Meta Events Manager',
  tutorialStep1Body:
    'Abra https://business.facebook.com/events_manager — logue com a conta que tem acesso ao Pixel do seu cliente. Se ainda não tem Pixel, crie um em "Conectar fontes de dados → Web → Pixel da Meta".',
  tutorialStep2Title: '2. Copie o Pixel ID',
  tutorialStep2Body:
    'No Events Manager, selecione o Pixel no menu lateral. O ID aparece logo abaixo do nome — é um número de 15-16 dígitos (ex: 1234567890123456). Cole no campo "Pixel ID" abaixo.',
  tutorialStep2bTitle: '2b. Copie o WhatsApp Business Account ID (page_id)',
  tutorialStep2bBody:
    'No Meta Business Manager → WhatsApp Manager → seu número/conta → Configurações da conta. O ID é um número de 15-17 dígitos. O Meta EXIGE esse parâmetro para eventos de CTWA (WhatsApp Ads); sem ele o test event falha com erro "page_id ausente". Cole no campo "WhatsApp Business Account ID" abaixo.',
  tutorialStep3Title: '3. Gere o Access Token (Conversions API)',
  tutorialStep3Body:
    'No mesmo Pixel, vá em Configurações → Conversions API → "Configurar manualmente" → "Gerar token de acesso". O token começa com "EAA..." e é mostrado uma única vez — copie e guarde em local seguro. Cole no campo "Access Token" abaixo.',
  tutorialStep4Title: '4. (Opcional) Gere um Test Event Code',
  tutorialStep4Body:
    'Em Events Manager → aba "Test Events" → digite um código (ex: TEST123) e clique "Generate Code". Use no campo "Código de teste" — eventos com esse código só aparecem na aba Test Events, sem impactar dados de produção. Remova depois de validar.',
  tutorialStep5Title: '5. Salve e teste',
  tutorialStep5Body:
    'Ative o toggle "Ativar integração", clique em "Salvar configuração", depois em "Enviar evento de teste". Em ~30s você deve ver o evento aparecer no Events Manager → Test Events com o code que setou.',
  tutorialStep6Title: '6. Como funciona em produção',
  tutorialStep6Body:
    'Depois de validar e remover o test_event_code: toda vez que uma conversa for marcada como "Ganho" no Kanban, dispara evento Purchase pro Meta com o valor do deal. Quando uma conversa receber a label "qualificado" (ou o label que você configurou), dispara evento Lead. Meta usa esses eventos pra atribuir conversões aos anúncios CTWA e otimizar campanhas.',
  tutorialLearnMoreLink:
    'https://www.facebook.com/business/help/2041148702652965',
  tutorialLearnMoreLabel: 'Documentação oficial do Meta Conversions API',
  enabled: 'Ativar integração',
  enabledHelp:
    'Quando ativa, eventos Purchase (deal Ganho) e Lead (label qualificado) são enviados ao Meta.',
  pixelId: 'Pixel ID',
  pixelIdHelp:
    'ID do Meta Pixel (Business Manager → Events Manager → Configurações)',
  pixelIdPlaceholder: '1234567890123456',
  pageId: 'WhatsApp Business Account ID (page_id)',
  pageIdHelp:
    'ID da conta do WhatsApp Business associada ao número (Meta exige para eventos CTWA). Encontre em Meta Business Manager → WhatsApp Manager → Configurações da conta. É um número de 15-17 dígitos.',
  pageIdPlaceholder: '1234567890123456',
  accessToken: 'Access Token',
  accessTokenPlaceholder: 'EAA...',
  accessTokenHelp:
    'Token gerado em Events Manager → Configurações → Configurar manualmente → Gerar token de acesso. Mantenha em segredo.',
  accessTokenSet: 'Token configurado (terminando em ...{last4})',
  accessTokenReplace: 'Substituir token',
  accessTokenLeaveEmpty:
    'Deixe em branco para manter o token atual. Preencha apenas para substituir.',
  testEventCode: 'Código de teste (opcional)',
  testEventCodeHelp:
    'Use durante testes para ver eventos em Events Manager → Test Events. Remova em produção.',
  testEventCodePlaceholder: 'TEST12345',
  leadQualifiedLabel: 'Label de qualificação (Lead)',
  leadQualifiedLabelHelp:
    'Quando uma conversa recebe este label, dispara evento Lead. Default: "qualificado".',
  leadQualifiedLabelPlaceholder: 'qualificado',
  save: 'Salvar configuração',
  saving: 'Salvando...',
  saveSuccess: 'Configuração Meta CAPI salva',
  saveError: 'Erro ao salvar configuração',
  testButton: 'Enviar evento de teste',
  testing: 'Enviando...',
  testHelp:
    'Dispara um evento Purchase de teste com a conversa mais recente que tenha deal_value preenchido.',
  testSuccess: 'Evento de teste enviado ao Meta',
  testNoCandidate:
    'Nenhuma conversa com deal_value encontrada para teste. Crie uma conversa de teste primeiro.',
  testError: 'Erro ao enviar evento de teste',
  statusActive: 'Ativa',
  statusInactive: 'Inativa',
  lastEvent: 'Último envio',
  noEventsYet: 'Nenhum evento enviado ainda.',
};

const store = useStore();
const account = useMapGetter('getCurrentAccount');

// Local copy of the meta_capi config. The profile/user serializer that
// runs at app boot does NOT include account.custom_attributes, so on a
// page refresh the Vuex account has no `meta_capi`. We fetch the masked
// config from a dedicated endpoint on mount and hydrate from this ref.
const fetchedMetaCapi = ref(null);

const form = ref({
  enabled: false,
  pixel_id: '',
  page_id: '',
  access_token: '',
  test_event_code: '',
  lead_qualified_label: 'qualificado',
});

const replaceToken = ref(false);
const isSaving = ref(false);
const isTesting = ref(false);
// Initialize true so the form renders immediately with defaults; we hydrate
// reactively when the account becomes available. Avoids spinning forever
// when a parent fetch silently fails or the account is already loaded.
const initialized = ref(true);

// Prefer the dedicated fetch (always fresh); fall back to whatever the
// account already has (e.g. when the user navigates here from another
// settings page within the same session).
const metaCapi = computed(
  () =>
    fetchedMetaCapi.value ||
    account.value?.custom_attributes?.meta_capi ||
    {}
);

const tokenLast4 = computed(() => metaCapi.value.access_token_last4 || '');
const tokenIsSet = computed(() => Boolean(metaCapi.value.access_token_set));
const lastEventAt = computed(() => metaCapi.value.last_event_at);
const lastEventStatus = computed(() => metaCapi.value.last_event_status);

const lastEventLabel = computed(() => {
  if (!lastEventAt.value) return LABELS.noEventsYet;
  const date = new Date(lastEventAt.value);
  const formatted = date.toLocaleString('pt-BR');
  const status = lastEventStatus.value || '—';
  return `${formatted} (${status})`;
});

// Tracks whether the user has manually edited the form. Once dirty, we
// stop auto-hydrating from the store so we don't clobber what they're
// typing (e.g. when account custom_attributes change due to other tabs
// or store updates).
const isDirty = ref(false);

const hydrate = () => {
  try {
    form.value.enabled = Boolean(metaCapi.value.enabled);
    form.value.pixel_id = metaCapi.value.pixel_id || '';
    form.value.page_id = metaCapi.value.page_id || '';
    form.value.test_event_code = metaCapi.value.test_event_code || '';
    form.value.lead_qualified_label =
      metaCapi.value.lead_qualified_label || 'qualificado';
    form.value.access_token = '';
    replaceToken.value = !tokenIsSet.value;
  } catch (e) {
    // Bug-fence: never let hydrate failures hide the form
    // eslint-disable-next-line no-console
    console.warn('[MetaCAPI] hydrate failed', e);
  }
};

// Initial hydration with whatever the store already has (instant render).
hydrate();

// Then refresh from the dedicated endpoint so a page reload picks up the
// real persisted config — the profile bootstrap doesn't include
// account.custom_attributes so we can't rely on Vuex here.
onMounted(async () => {
  try {
    const { meta_capi: data } = await store.dispatch(
      'integrations/fetchMetaCapi'
    );
    fetchedMetaCapi.value = data || {};
    if (!isDirty.value) hydrate();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[MetaCAPI] fetch failed', e);
  }
});

// Watch for genuine changes in the saved meta_capi config. Critically:
// - No 'immediate' (initial hydrate already happened above)
// - No 'deep' (avoids reference-only triggers from {} literals in computeds)
// - Skip when the form is dirty (user typing) so we never wipe input
watch(
  () => metaCapi.value,
  () => {
    if (isDirty.value) return;
    hydrate();
  }
);

// Mark dirty when the user actually interacts with a field (template
// calls markDirty via @input/@change). Using watch(form) would fire on
// hydrate() too and falsely mark the form dirty before any user input.
const markDirty = () => {
  isDirty.value = true;
};

const buildPayload = () => {
  const payload = {
    enabled: form.value.enabled,
    pixel_id: form.value.pixel_id.trim(),
    page_id: form.value.page_id.trim() || null,
    test_event_code: form.value.test_event_code.trim() || null,
    lead_qualified_label:
      form.value.lead_qualified_label.trim() || 'qualificado',
  };
  if (replaceToken.value && form.value.access_token.trim()) {
    payload.access_token = form.value.access_token.trim();
  } else if (tokenIsSet.value) {
    // Backend will keep existing token when key is absent
  }
  return payload;
};

const save = async () => {
  if (isSaving.value) return;
  isSaving.value = true;
  try {
    const payload = buildPayload();
    const updated = await store.dispatch(
      'integrations/updateMetaCapi',
      payload
    );
    const metaCapiResponse = updated?.meta_capi;
    // Persist the sanitized response locally so hydrate() reads from it
    // on the next render. Also patch the Vuex account in case other
    // parts of the UI read from it within this session.
    if (metaCapiResponse) {
      fetchedMetaCapi.value = metaCapiResponse;
    }
    if (account.value && metaCapiResponse) {
      account.value.custom_attributes = {
        ...(account.value.custom_attributes || {}),
        meta_capi: metaCapiResponse,
      };
    }
    // After a successful save, treat the form as clean again and let
    // hydrate re-run with the canonical server response (e.g. updated
    // access_token_last4, last_event_at).
    isDirty.value = false;
    hydrate();
    useAlert(LABELS.saveSuccess);
    replaceToken.value = false;
    form.value.access_token = '';
  } catch (e) {
    useAlert(`${LABELS.saveError}: ${e.message || ''}`);
  } finally {
    isSaving.value = false;
  }
};

const sendTestEvent = async () => {
  if (isTesting.value) return;
  isTesting.value = true;
  try {
    await store.dispatch('integrations/sendMetaCapiTestEvent');
    useAlert(LABELS.testSuccess);
  } catch (e) {
    const reason = e?.response?.data?.error || e?.message;
    if (reason === 'no_candidate') {
      useAlert(LABELS.testNoCandidate);
    } else {
      useAlert(`${LABELS.testError}: ${reason || ''}`);
    }
  } finally {
    isTesting.value = false;
  }
};

const tutorialSteps = [
  {
    number: 1,
    titleNoNumber: LABELS.tutorialStep1Title.replace(/^\d+\.\s*/, ''),
    body: LABELS.tutorialStep1Body,
  },
  {
    number: 2,
    titleNoNumber: LABELS.tutorialStep2Title.replace(/^\d+\.\s*/, ''),
    body: LABELS.tutorialStep2Body,
  },
  {
    number: '2b',
    titleNoNumber: LABELS.tutorialStep2bTitle.replace(/^\d+b?\.\s*/, ''),
    body: LABELS.tutorialStep2bBody,
  },
  {
    number: 3,
    titleNoNumber: LABELS.tutorialStep3Title.replace(/^\d+\.\s*/, ''),
    body: LABELS.tutorialStep3Body,
  },
  {
    number: 4,
    titleNoNumber: LABELS.tutorialStep4Title.replace(/^\d+\.\s*/, ''),
    body: LABELS.tutorialStep4Body,
  },
  {
    number: 5,
    titleNoNumber: LABELS.tutorialStep5Title.replace(/^\d+\.\s*/, ''),
    body: LABELS.tutorialStep5Body,
  },
  {
    number: 6,
    titleNoNumber: LABELS.tutorialStep6Title.replace(/^\d+\.\s*/, ''),
    body: LABELS.tutorialStep6Body,
  },
];

const statusBadge = computed(() =>
  form.value.enabled ? LABELS.statusActive : LABELS.statusInactive
);
const statusBadgeClass = computed(() =>
  form.value.enabled
    ? 'bg-emerald-500/10 text-emerald-600 border-emerald-600/30'
    : 'bg-n-alpha-2 text-n-slate-11 border-n-weak'
);
</script>

<template>
  <SettingsLayout :is-loading="!initialized">
    <template #header>
      <BaseSettingsHeader
        :title="LABELS.title"
        :description="LABELS.description"
        :back-button-label="LABELS.backToIntegrations"
      />
    </template>
    <template #body>
      <div class="max-w-3xl mx-auto p-6 space-y-6">
        <section
          class="rounded-xl border border-n-weak bg-n-alpha-1 p-6 space-y-4"
        >
          <h2 class="text-base font-semibold text-n-slate-12 m-0">
            {{ LABELS.sectionTutorial }}
          </h2>
          <p class="text-sm text-n-slate-11 m-0">
            {{ LABELS.tutorialIntro }}
          </p>
          <ol class="space-y-4 list-none m-0 p-0">
            <li
              v-for="step in tutorialSteps"
              :key="step.title"
              class="flex gap-3"
            >
              <div
                class="flex-shrink-0 w-7 h-7 rounded-full bg-woot-500 text-white text-xs font-bold flex items-center justify-center mt-0.5"
              >
                {{ step.number }}
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold text-n-slate-12 m-0 mb-1">
                  {{ step.titleNoNumber }}
                </p>
                <p class="text-sm text-n-slate-11 m-0 leading-relaxed">
                  {{ step.body }}
                </p>
              </div>
            </li>
          </ol>
          <a
            :href="LABELS.tutorialLearnMoreLink"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1 text-sm text-woot-500 hover:text-woot-600"
          >
            <span class="i-lucide-external-link w-4 h-4" />
            {{ LABELS.tutorialLearnMoreLabel }}
          </a>
        </section>

        <section
          class="rounded-xl border border-n-weak bg-n-surface-1 p-6 space-y-4"
        >
          <header class="flex items-center justify-between">
            <h2 class="text-base font-semibold text-n-slate-12 m-0">
              {{ LABELS.sectionConfig }}
            </h2>
            <span
              class="px-2 py-0.5 text-xs font-semibold rounded-full border"
              :class="statusBadgeClass"
            >
              {{ statusBadge }}
            </span>
          </header>

          <label
            class="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-n-alpha-1 transition-colors"
          >
            <input
              v-model="form.enabled"
              type="checkbox"
              class="mt-1"
              @change="markDirty"
            />
            <div>
              <p class="text-sm font-medium text-n-slate-12 m-0">
                {{ LABELS.enabled }}
              </p>
              <p class="text-xs text-n-slate-11 m-0 mt-1">
                {{ LABELS.enabledHelp }}
              </p>
            </div>
          </label>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ LABELS.pixelId }}
            </label>
            <input
              v-model="form.pixel_id"
              type="text"
              :placeholder="LABELS.pixelIdPlaceholder"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12"
              @input="markDirty"
            />
            <p class="text-xs text-n-slate-11 m-0 mt-1">
              {{ LABELS.pixelIdHelp }}
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ LABELS.pageId }}
            </label>
            <input
              v-model="form.page_id"
              type="text"
              :placeholder="LABELS.pageIdPlaceholder"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12"
              @input="markDirty"
            />
            <p class="text-xs text-n-slate-11 m-0 mt-1">
              {{ LABELS.pageIdHelp }}
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ LABELS.accessToken }}
            </label>
            <div
              v-if="tokenIsSet && !replaceToken"
              class="flex items-center justify-between p-3 rounded-lg border border-n-weak bg-n-alpha-1"
            >
              <span class="text-sm text-n-slate-11">
                {{
                  LABELS.accessTokenSet.replace('{last4}', tokenLast4 || '****')
                }}
              </span>
              <button
                type="button"
                class="text-xs font-semibold text-woot-500 hover:text-woot-600 bg-transparent border-0 cursor-pointer"
                @click="replaceToken = true"
              >
                {{ LABELS.accessTokenReplace }}
              </button>
            </div>
            <input
              v-else
              v-model="form.access_token"
              type="password"
              autocomplete="off"
              :placeholder="LABELS.accessTokenPlaceholder"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 font-mono"
              @input="markDirty"
            />
            <p class="text-xs text-n-slate-11 m-0 mt-1">
              {{
                tokenIsSet
                  ? LABELS.accessTokenLeaveEmpty
                  : LABELS.accessTokenHelp
              }}
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ LABELS.testEventCode }}
            </label>
            <input
              v-model="form.test_event_code"
              type="text"
              :placeholder="LABELS.testEventCodePlaceholder"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 font-mono"
              @input="markDirty"
            />
            <p class="text-xs text-n-slate-11 m-0 mt-1">
              {{ LABELS.testEventCodeHelp }}
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ LABELS.leadQualifiedLabel }}
            </label>
            <input
              v-model="form.lead_qualified_label"
              type="text"
              :placeholder="LABELS.leadQualifiedLabelPlaceholder"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12"
              @input="markDirty"
            />
            <p class="text-xs text-n-slate-11 m-0 mt-1">
              {{ LABELS.leadQualifiedLabelHelp }}
            </p>
          </div>

          <div class="flex items-center gap-3 pt-2">
            <button
              type="button"
              class="px-4 py-2 text-sm font-semibold bg-woot-500 text-white rounded-lg border-0 cursor-pointer hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="isSaving"
              @click="save"
            >
              {{ isSaving ? LABELS.saving : LABELS.save }}
            </button>
            <button
              type="button"
              class="px-4 py-2 text-sm font-semibold text-n-slate-12 bg-n-button-color border border-n-weak rounded-lg cursor-pointer hover:bg-n-alpha-2 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="isTesting || !form.enabled || !form.pixel_id"
              @click="sendTestEvent"
            >
              {{ isTesting ? LABELS.testing : LABELS.testButton }}
            </button>
          </div>
          <p class="text-xs text-n-slate-11 m-0 mt-1">
            {{ LABELS.testHelp }}
          </p>
        </section>

        <section class="rounded-xl border border-n-weak bg-n-surface-1 p-6">
          <h2 class="text-base font-semibold text-n-slate-12 m-0 mb-3">
            {{ LABELS.sectionEvents }}
          </h2>
          <p class="text-sm text-n-slate-11 m-0">
            <strong class="text-n-slate-12">{{ LABELS.lastEvent }}:</strong>
            {{ lastEventLabel }}
          </p>
        </section>
      </div>
    </template>
  </SettingsLayout>
</template>
